import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import ConfirmAction from "../../Components/Utils/ConfirmAction/ConfirmAction";
import LoadingIcon from "../../Components/Utils/LoadingIcon/LoadingIcon";
import {
  getLibraryImplicitAccess,
  LibraryImplicitAccessUser,
  LibraryPortfolioView,
  setLibraryAccess,
} from "../../API/Library/LibraryCommands";
import { libraryErrorMessage } from "./libraryUtils";
import styles from "./PortfolioAccessPreview.module.css";

type PortfolioAccessPreviewProps = {
  portfolioId: string;
  clientId: string;
  access: string;
  ownerName: string;
  isOwner: boolean;
  canOpenWhenPrivate: boolean;
  onChanged: (portfolio: LibraryPortfolioView) => void;
  onLeave: () => void;
};

const PortfolioAccessPreview = ({
  portfolioId,
  clientId,
  access,
  ownerName,
  isOwner,
  canOpenWhenPrivate,
  onChanged,
  onLeave,
}: PortfolioAccessPreviewProps) => {
  const global = access === "Global";
  const [users, setUsers] = useState<LibraryImplicitAccessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [confirmPrivate, setConfirmPrivate] = useState(false);
  const [confirmGlobal, setConfirmGlobal] = useState(false);
  const { user } = useAuth0();
  const myName = user?.name || [user?.given_name, user?.family_name].filter(Boolean).join(" ") || "You";

  useEffect(() => {
    let active = true;
    setLoading(true);
    getLibraryImplicitAccess(portfolioId, clientId)
      .then((rows) => {
        if (active) setUsers(rows);
      })
      .catch((error) => {
        if (active) toast.error(libraryErrorMessage(error, "Access preview could not be loaded."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [portfolioId, clientId, access]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) =>
      user.fullName.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle)
    );
  }, [users, query]);

  const apply = async (next: "Private" | "Global") => {
    setSaving(true);
    try {
      const updated = await setLibraryAccess(portfolioId, clientId, next);
      onChanged(updated);
      if (next === "Private" && !updated.canOpenWhenPrivate) onLeave();
      else toast.success(next === "Global" ? "Everyone in the client can now read this portfolio" : "This portfolio is private again");
    } catch (error) {
      toast.error(libraryErrorMessage(error, "Access could not be updated."));
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (checked: boolean) => {
    if (checked && !isOwner) {
      setConfirmGlobal(true);
      return;
    }
    if (!checked && !canOpenWhenPrivate) {
      setConfirmPrivate(true);
      return;
    }
    void apply(checked ? "Global" : "Private");
  };

  return (
    <section className={styles.panel} aria-labelledby="portfolio-access-preview-title">
      <div className={styles.panelHeader}>
        <span className={styles.label}>Access preview</span>
        <p className={styles.adminNote}>You can see this because you are an administrator.</p>
      </div>
      <h4 id="portfolio-access-preview-title">Who can access this portfolio without an invite?</h4>
      <label className={styles.check}>
        <input
          type="checkbox"
          checked={global}
          disabled={saving}
          onChange={(event) => onToggle(event.target.checked)}
        />
        <span>Make this portfolio global so everyone in the client can read it</span>
      </label>
      <p className={styles.intro}>
        {global
          ? "Everyone in the client can open this portfolio and download files. They are not added as readers, and they do not appear in the portfolio users list. Removing someone from the portfolio does not remove their access while it is global. Invite someone as a Participant to let them edit."
          : access === "Public"
            ? "Advanced Admins can open, edit, invite, and delete this portfolio without an invite. If it is attached to a partner or department, members of that partner or department can open it and download files."
            : "Advanced Admins can open, edit, invite, and delete this portfolio without an invite."}
      </p>
      <div className={styles.toolbar}>
        <input
          value={query}
          placeholder="Search by name or email"
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search people with access"
        />
        <span>{loading ? "…" : `${visible.length} ${visible.length === 1 ? "person" : "people"}`}</span>
      </div>
      {loading ? (
        <div className={styles.loading}><LoadingIcon /></div>
      ) : visible.length > 0 ? (
        <ul className={styles.list}>
          {visible.map((user) => (
            <li key={user.sub}>
              <div>
                <strong>{user.fullName || user.email || user.sub}</strong>
                {user.fullName && user.email ? <span>{user.email}</span> : null}
              </div>
              <em className={user.roleLabel === "Reader" ? undefined : styles.accent}>{user.roleLabel}</em>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.empty}>{query.trim() ? "No one matches that search." : "No one has access without an invite."}</p>
      )}
      <ConfirmAction
        isVisible={confirmGlobal}
        onClose={() => setConfirmGlobal(false)}
        clickHandler={() => apply("Global")}
        title="Make this portfolio global?"
        message="You must be the owner of the portfolio and an administrator to change the access type. Transfer ownership to you?"
        confirmText="Transfer ownership"
        severity="warning"
      >
        <div className={styles.transfer}>
          <div className={styles.transferPerson}>
            <span>Current owner</span>
            <strong>{ownerName || "Current owner"}</strong>
          </div>
          <img src="/img/down-arrow.svg" alt="" />
          <div className={styles.transferPerson}>
            <span>New owner</span>
            <strong>{myName}</strong>
          </div>
        </div>
      </ConfirmAction>
      <ConfirmAction
        isVisible={confirmPrivate}
        onClose={() => setConfirmPrivate(false)}
        clickHandler={() => apply("Private")}
        title="Make this portfolio private?"
        message="You are not a member of this portfolio. Making it private will remove your access."
        confirmText="Make private"
        severity="warning"
      />
    </section>
  );
};

export default PortfolioAccessPreview;
